import posthog from '../posthog';
import { isProdHost } from '../utils/environment';

// How the consumer reached the widget. Preview wins over the rest: it describes
// who is visiting (a Cinch employee checking a calendar) rather than the
// mechanics of the page, and those visits are excluded from consumer analysis.
export const ARRIVAL_PATHS = {
  PREVIEW: 'preview',
  RESERVE_WITH_GOOGLE: 'reserve_with_google',
  EMBED: 'embed',
  DIRECT_LINK: 'direct_link',
};

export const CONSUMER_EXPERIENCE = 'booking_widget';

// The Cinch app links to a calendar from its setup wizard. The explicit marker
// is what the app appends; the referrer check is a fallback for links that lose
// it (a copied URL, rel=noreferrer, a privacy setting that strips referrers),
// so preview traffic is under-reported rather than mislabelled as a consumer.
const CINCH_APP_HOSTS = ['marketing.cinch.io', 'marketing-dev.cinch.io', 'beta.marketing.cinch.io'];

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top across origins throws, which itself proves we are
    // embedded on someone else's page.
    return true;
  }
};

const referrerHost = () => {
  try {
    return document.referrer ? new URL(document.referrer).hostname : '';
  } catch {
    return '';
  }
};

const isPreview = (params) =>
  params.get('source') === 'preview' || CINCH_APP_HOSTS.includes(referrerHost());

export const resolveArrivalPath = (params, hasRwgToken) => {
  if (isPreview(params)) return ARRIVAL_PATHS.PREVIEW;
  if (hasRwgToken) return ARRIVAL_PATHS.RESERVE_WITH_GOOGLE;
  if (isInIframe()) return ARRIVAL_PATHS.EMBED;
  return ARRIVAL_PATHS.DIRECT_LINK;
};

// The site the widget is running on. Embedded, that is the parent page, which
// is only visible through the referrer; standalone, it is our own host.
export const resolveHostDomain = () => (isInIframe() ? referrerHost() : window.location.hostname);

// Cinch's own demo pages ship with the widget, so they are ours by definition.
const isCinchDemoPage = () =>
  /^\/demo(-simple)?\.html$/.test(window.location.pathname) || window.location.pathname === '/icons';

// Internal when the calendar belongs to Cinch (the server decides, since the
// browser cannot), when an employee is previewing, or on our own demo pages.
export const resolveIsInternal = ({ calendarIsInternal, arrivalPath }) =>
  !!calendarIsInternal || arrivalPath === ARRIVAL_PATHS.PREVIEW || isCinchDemoPage();

// Properties every event carries. Registered rather than passed per call so a
// new event cannot forget them. Called again as later facts arrive — the
// booking session id only exists once the server issues it.
export const registerWidgetContext = (properties) => {
  const defined = Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined));
  posthog.register({ consumer_experience: CONSUMER_EXPERIENCE, ...defined });
};

// Non-production builds report to the Sandbox project; this says so on the
// events themselves, so a glance at one tells you where it came from.
export const widgetEnvironmentProperties = () => ({
  is_production_widget: isProdHost(),
});
